import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import versionCheckService from '../services/versionCheck.service';
import { initVersionBroadcast, closeVersionBroadcast } from '../utils/versionOutdated';
import VersionOutdatedOverlay from '../components/VersionOutdated/VersionOutdatedOverlay';
import authService from '../services/auth.service';

interface VersionCheckContextValue {
  isOutdated: boolean;
  currentVersion: string | null;
  latestVersion: string | null;
}

const VersionCheckContext = createContext<VersionCheckContextValue | undefined>(undefined);

export const useVersionCheck = () => {
  const context = useContext(VersionCheckContext);
  if (!context) {
    throw new Error('useVersionCheck must be used within a VersionCheckProvider');
  }
  return context;
};

interface VersionCheckProviderProps {
  children: ReactNode;
}

export const VersionCheckProvider: React.FC<VersionCheckProviderProps> = ({ children }) => {
  const [isOutdated, setIsOutdated] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  useEffect(() => {
    try {
      // Получить текущую версию
      const current = versionCheckService.getCurrentVersion();
      setCurrentVersion(current);

      // Callback при обнаружении устаревшей версии (для BroadcastChannel)
      const handleOutdatedFromBroadcast = () => {
        console.log('[VersionCheckContext] Версия устарела (от другой вкладки)!');
        setIsOutdated(true);

        // Вызвать logout и перенаправить на страницу логина
        // с небольшой задержкой, чтобы пользователь увидел overlay
        setTimeout(() => {
          authService.logout()
            .then(() => {
              window.location.href = '/login?outdated=true';
            })
            .catch((error) => {
              console.error('[VersionCheckContext] Ошибка при logout', error);
              window.location.href = '/login?outdated=true';
            });
        }, 2000); // 2 секунды на показ overlay
      };

      // Callback при обнаружении устаревшей версии (для versionCheckService)
      const handleOutdated = (current: string, latest: string) => {
        console.log('[VersionCheckContext] Версия устарела!', { current, latest });
        setIsOutdated(true);
        setCurrentVersion(current);
        setLatestVersion(latest);
        handleOutdatedFromBroadcast();
      };

      // Инициализировать BroadcastChannel для синхронизации между вкладками
      initVersionBroadcast(handleOutdatedFromBroadcast);

      // Запустить периодическую проверку версии
      versionCheckService.startPeriodicCheck({
        onOutdated: handleOutdated,
      });

      console.log('[VersionCheckContext] Проверка версий инициализирована', {
        currentVersion: current,
      });
    } catch (error) {
      console.error('[VersionCheckContext] Ошибка инициализации', error);
    }

    // Cleanup при размонтировании
    return () => {
      versionCheckService.stopPeriodicCheck();
      closeVersionBroadcast();
    };
  }, []);

  const value: VersionCheckContextValue = {
    isOutdated,
    currentVersion,
    latestVersion,
  };

  return (
    <VersionCheckContext.Provider value={value}>
      {children}
      <VersionOutdatedOverlay
        isVisible={isOutdated}
        currentVersion={currentVersion || undefined}
        latestVersion={latestVersion || undefined}
      />
    </VersionCheckContext.Provider>
  );
};
